import { useEffect } from 'react';
import { useCards } from '@/contexts/cards';
import { useAccounts } from '@/contexts/accounts';

interface UseTransactionFormEffectsProps {
  formData: any;
  setFormData: (updater: (prev: any) => any) => void;
  categories: any[];
  errors: Record<string, string>;
  clearFieldError: (field: string) => void;
  isEditing?: boolean;
}

export const useTransactionFormEffects = ({
  formData,
  setFormData,
  categories,
  errors,
  clearFieldError,
  isEditing = false
}: UseTransactionFormEffectsProps) => {
  const { cards } = useCards();
  const { accounts } = useAccounts();

  // Reset categoryId if current category is not valid for current type
  useEffect(() => {
    if (formData.categoryId && categories.length > 0) {
      const currentCategory = categories.find(cat => cat.id === formData.categoryId);
      if (!currentCategory || currentCategory.type !== formData.type) {
        setFormData(prev => ({ ...prev, categoryId: '' }));
      }
    }
  }, [formData.type, formData.categoryId, categories, setFormData]);

  // Clear cardId when payment method changes from 'cartao' or when type is 'receita' and payment method is 'cartao'
  useEffect(() => {
    if (formData.paymentMethod !== 'cartao' || (formData.type === 'receita' && formData.paymentMethod === 'cartao')) {
      if (formData.cardId) {
        setFormData(prev => ({ ...prev, cardId: '' }));
        
        // Clear related errors
        if (errors.cardId) {
          clearFieldError('cardId');
        }
      }
    }
  }, [formData.paymentMethod, formData.type, formData.cardId, setFormData, errors.cardId, clearFieldError]);

  // Reset installment settings when payment method doesn't support installments
  useEffect(() => {
    const supportedMethods = ['dinheiro', 'cartao', 'pix', 'boleto'];
    if (!supportedMethods.includes(formData.paymentMethod)) {
      if (formData.isInstallment) {
        setFormData(prev => ({
          ...prev,
          isInstallment: false,
          installments: 2,
          installmentDescription: ''
        }));
      }
    }
  }, [formData.paymentMethod, formData.isInstallment, setFormData]);

  // Force 'pending' status for credit card expenses - ONLY for new transactions
  useEffect(() => {
    if (!isEditing && formData.paymentMethod === 'cartao' && formData.type === 'despesa') {
      if (formData.status !== 'pending') {
        setFormData(prev => ({ ...prev, status: 'pending' }));
      }
    }
  }, [formData.paymentMethod, formData.type, formData.status, setFormData, isEditing]);

  // Auto-populate account based on payment method, type, and selected cardId or accountId
  useEffect(() => {
    if (formData.paymentMethod === 'cartao' && formData.cardId && cards.length > 0) {
      const selectedCard = cards.find(card => card.id === formData.cardId);
      if (selectedCard) {
        const accountDisplay = selectedCard.accountId
          ? (() => {
              const linkedAccount = accounts.find(acc => acc.id === selectedCard.accountId);
              return linkedAccount ? `${selectedCard.name} (${linkedAccount.name})` : selectedCard.name;
            })()
          : selectedCard.name;
        
        setFormData(prev => ({
          ...prev,
          account: accountDisplay,
          accountId: selectedCard.accountId || ''
        }));
      }
    } else if (formData.paymentMethod !== 'cartao' && formData.accountId && accounts.length > 0) {
      const selectedAccount = accounts.find(acc => acc.id === formData.accountId);
      if (selectedAccount) {
        setFormData(prev => ({
          ...prev,
          account: selectedAccount.name
        }));
      }
    }
  }, [
    formData.paymentMethod, 
    formData.cardId, 
    formData.accountId, 
    cards, 
    accounts, 
    setFormData
  ]);
};

// Export as default export for compatibility  
export default useTransactionFormEffects;